import {
  AccountTree as PipelineIcon,
  Upload as UploadIcon,
  Schema as SchemaIcon,
  FolderOpen as ProjectIcon,
  QueryStats as ExploreIcon,
  Transform as TransformIcon,
  Hub as MappingIcon,
  Verified as ReviewIcon,
  CloudUpload as ExportIcon,
  Schedule as ScheduleIcon,
  Cable as ConnectorIcon,
  Keyboard as KeyboardIcon,
  BugReport as TroubleshootIcon,
  MenuBook as GlossaryIcon,
} from '@mui/icons-material';
import { Box, Typography, Paper, Divider, Link, Stack, alpha, useTheme } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-ignore — remark-gfm v4 is ESM-only; types resolve at runtime
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { GUIDE_SECTIONS } from './help/guideContent.generated';

// ─── Per-section icons (keyed by the stable section id) ──────────
const SECTION_ICONS: Record<string, React.ReactNode> = {
  'getting-started': <PipelineIcon />,
  'uploading-data': <UploadIcon />,
  'schema-management': <SchemaIcon />,
  'projects-batches': <ProjectIcon />,
  'exploring-data': <ExploreIcon />,
  'building-pipelines': <TransformIcon />,
  'mapping-fields': <MappingIcon />,
  'quality-review': <ReviewIcon />,
  exporting: <ExportIcon />,
  scheduling: <ScheduleIcon />,
  connectors: <ConnectorIcon />,
  'keyboard-shortcuts': <KeyboardIcon />,
  troubleshooting: <TroubleshootIcon />,
  glossary: <GlossaryIcon />,
};

const SectionHeading: React.FC<{ id: string; icon: React.ReactNode; title: string }> = ({
  id,
  icon,
  title,
}) => {
  const theme = useTheme();
  return (
    <Stack
      id={id}
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ mb: 2, mt: 4, scrollMarginTop: 80 }}
    >
      <Box
        sx={{
          p: 0.75,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          color: 'primary.main',
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
};

// ─── Markdown styling for guide bodies ───────────────────────────
const markdownSx = {
  '& p': { mt: 0, mb: 1.5, lineHeight: 1.6 },
  '& h2': { mt: 3, mb: 1.5, fontWeight: 700, fontSize: '1.25rem' },
  '& h3': { mt: 2.5, mb: 1, fontWeight: 700, fontSize: '1.1rem' },
  '& h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 600, fontSize: '1rem' },
  '& ul, & ol': { mt: 0, mb: 1.5, pl: 3 },
  '& li': { mb: 0.5, lineHeight: 1.6 },
  '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
  '& strong': { fontWeight: 600 },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    my: 1.5,
    display: 'block',
  },
  '& blockquote': {
    borderLeft: '3px solid',
    borderColor: 'primary.light',
    bgcolor: 'action.hover',
    px: 2,
    py: 1,
    my: 1.5,
    ml: 0,
    borderRadius: '0 4px 4px 0',
    '& p': { m: 0 },
  },
  '& code': {
    fontFamily: 'monospace',
    bgcolor: 'action.hover',
    px: 0.5,
    borderRadius: 0.5,
    fontSize: '0.875em',
  },
  '& pre': {
    m: 0,
    mb: 1.5,
    p: 1.5,
    borderRadius: 1,
    bgcolor: 'action.hover',
    overflowX: 'auto',
    '& code': { bgcolor: 'transparent', p: 0 },
  },
  '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 2 },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    my: 1.5,
    fontSize: '0.875rem',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    px: 1.5,
    py: 0.75,
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& th': { bgcolor: 'action.hover', fontWeight: 600 },
} as const;

// ─── Table of Contents ───────────────────────────────────────────
const TOC_GROUPS = [
  { label: 'Get Started', ids: ['getting-started'] },
  { label: 'Ingest', ids: ['uploading-data', 'connectors'] },
  { label: 'Define', ids: ['schema-management'] },
  { label: 'Organize', ids: ['projects-batches'] },
  { label: 'Explore', ids: ['exploring-data'] },
  { label: 'Transform', ids: ['building-pipelines'] },
  { label: 'Map', ids: ['mapping-fields'] },
  { label: 'Review', ids: ['quality-review'] },
  { label: 'Export', ids: ['exporting', 'scheduling'] },
  { label: 'Reference', ids: ['keyboard-shortcuts', 'troubleshooting', 'glossary'] },
];

// ─── Page component ──────────────────────────────────────────────
const HelpPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hash } = useLocation();

  const scrollToId = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (hash) scrollToId(hash.replace('#', ''));
  }, [hash, scrollToId]);

  // Custom anchor renderer: in-page #links scroll smoothly and update the URL
  // hash; external links open in a new tab.
  const renderLink = useCallback(
    ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      if (href && href.startsWith('#')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              const id = href.slice(1);
              scrollToId(id);
              navigate(`/help#${id}`, { replace: false });
            }}
            {...props}
          >
            {children}
          </a>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
    [navigate, scrollToId],
  );

  return (
    <Box sx={{ display: 'flex', maxWidth: 1200, mx: 'auto', py: 4, px: 3, gap: 4 }}>
      {/* Sticky sidebar TOC */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          position: 'sticky',
          top: 80,
          alignSelf: 'flex-start',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Contents
        </Typography>
        {TOC_GROUPS.map((g) => (
          <Box key={g.label} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              {g.label}
            </Typography>
            {g.ids.map((id) => {
              const sec = GUIDE_SECTIONS.find((s) => s.id === id);
              return sec ? (
                <Link
                  key={id}
                  href={`#${id}`}
                  underline="hover"
                  color="text.primary"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToId(id);
                    navigate(`/help#${id}`);
                  }}
                  sx={{
                    display: 'block',
                    fontSize: 13,
                    py: 0.25,
                    pl: 1,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {sec.title}
                </Link>
              ) : null;
            })}
          </Box>
        ))}
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Migratr User Guide
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Everything you need to know about using the platform — from uploading
          data to exporting production-ready pipelines.
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {GUIDE_SECTIONS.map((sec) => (
          <Paper
            key={sec.id}
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              '&:target': {
                borderColor: 'primary.main',
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
            }}
          >
            <SectionHeading id={sec.id} icon={SECTION_ICONS[sec.id]} title={sec.title} />
            <Box sx={markdownSx}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{ a: renderLink }}
              >
                {sec.markdown}
              </ReactMarkdown>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default HelpPage;
